/**
 * PublicPortalForgotPasswordPage — `/c/<slug>/forgot-password` (no auth)
 *
 * Two-step branded reset flow:
 *   1) Ask for email → POST /api/public/portal/{slug}/forgot-password
 *      (sends 6-digit code via Resend, scoped to this membership)
 *   2) Enter code + new password → POST /api/public/portal/{slug}/reset-password
 *      → membership password_hash is updated, session is issued, redirect to /portal.
 *
 * The reset only affects this membership — any other CountBucks logins
 * for the same email are unchanged. This is what the per-membership
 * password model gives us.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2, Mail, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { setSessionToken } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicPortalForgotPasswordPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [branding, setBranding] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [step, setStep] = useState('request'); // 'request' | 'reset'
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/portal/${slug}`);
        if (r.status === 404) { setNotFound(true); return; }
        if (!r.ok) throw new Error('failed');
        setBranding(await r.json());
      } catch {
        setNotFound(true);
      } finally {
        setBrandingLoading(false);
      }
    })();
  }, [slug]);

  const requestCode = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!email) { setError('Please enter your email.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/portal/${slug}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.detail || 'Failed to request reset.');
        setSubmitting(false);
        return;
      }
      setInfo(data.message || 'Check your email for the reset code.');
      if (data.dev_code) {
        // Dev fallback — show code inline if email delivery failed.
        setCode(data.dev_code);
      }
      setStep('reset');
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!code || !newPassword) {
      setError('Please enter the code and your new password.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/portal/${slug}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          code: code.trim(),
          new_password: newPassword,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.detail || 'Failed to reset password.');
        setSubmitting(false);
        return;
      }
      if (data.token) {
        setSessionToken(data.token);
      }
      navigate('/portal', { replace: true });
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
      setSubmitting(false);
    }
  };

  if (brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (notFound || !branding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold mb-3">Portal not found</h1>
          <p className="text-zinc-600 mb-6">
            We couldn't find a client portal at this address.
          </p>
          <Link to="/" className="text-emerald-600 hover:underline">Go home</Link>
        </div>
      </div>
    );
  }

  const brand = branding.brand_color || '#10b981';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50 flex flex-col" data-testid="portal-forgot-password-page">
      <header className="border-b" style={{ borderColor: `${brand}22` }}>
        <div className="max-w-5xl mx-auto w-full px-6 py-5 flex items-center justify-between">
          <Link to={`/c/${slug}`} className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ background: brand }}
              >
                {(branding.name || '?').charAt(0)}
              </div>
            )}
            <div>
              <p className="font-semibold text-lg leading-none">{branding.name}</p>
              <p className="text-xs text-zinc-500 mt-1">Client Portal</p>
            </div>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8">
            {step === 'request' && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-5 h-5" style={{ color: brand }} />
                  <h1 className="text-2xl font-bold text-zinc-900">Reset your portal password</h1>
                </div>
                <p className="text-sm text-zinc-600 mb-6">
                  Enter the email you use for <strong>{branding.name}</strong>'s portal and we'll send you a 6-digit code.
                </p>
                <form onSubmit={requestCode} className="space-y-4" data-testid="portal-forgot-request-form">
                  <div>
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="mt-1"
                      data-testid="portal-forgot-email"
                    />
                  </div>
                  {error && (
                    <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="portal-forgot-error">
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 text-base text-white shadow-md hover:shadow-lg transition-shadow"
                    style={{ background: brand }}
                    data-testid="portal-forgot-send-code"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Send code
                  </Button>
                </form>
              </>
            )}

            {step === 'reset' && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <KeyRound className="w-5 h-5" style={{ color: brand }} />
                  <h1 className="text-2xl font-bold text-zinc-900">Enter your code</h1>
                </div>
                <p className="text-sm text-zinc-600 mb-6">
                  {info || `We sent a 6-digit code to ${email}. Enter it below along with your new password.`}
                </p>
                <form onSubmit={submitReset} className="space-y-4" data-testid="portal-forgot-reset-form">
                  <div>
                    <Label htmlFor="reset-code">6-digit code</Label>
                    <Input
                      id="reset-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      className="mt-1 tracking-widest text-center text-lg"
                      maxLength={6}
                      data-testid="portal-forgot-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="mt-1"
                      minLength={8}
                      data-testid="portal-forgot-new-password"
                    />
                    <p className="text-xs text-zinc-500 mt-1">At least 8 characters.</p>
                  </div>
                  {error && (
                    <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="portal-forgot-error">
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 text-base text-white shadow-md hover:shadow-lg transition-shadow"
                    style={{ background: brand }}
                    data-testid="portal-forgot-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Reset password &amp; sign in
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setStep('request'); setCode(''); setNewPassword(''); setError(''); }}
                    className="w-full text-sm text-zinc-500 hover:text-zinc-700"
                    data-testid="portal-forgot-back"
                  >
                    ← Send the code again
                  </button>
                </form>
              </>
            )}

            <div className="mt-6 text-center text-xs text-zinc-500">
              <Link to={`/c/${slug}/login`} className="font-medium hover:underline" style={{ color: brand }}>
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 text-xs text-zinc-500 text-center">
          Powered by{' '}
          <a href="https://countbucks.com" className="font-semibold text-zinc-700 hover:underline">CountBucks</a>
        </div>
      </footer>
    </div>
  );
}
